// @ts-check
// Grades da agenda: mês (6 semanas, até 3 itens por dia e "+N eventos") e grade de horas para semana e dia, com os
// itens lado a lado quando os horários se sobrepõem e uma faixa de dia inteiro no topo.
import { html } from 'htm/preact';
import { useEffect, useRef } from 'preact/hooks';
import {
  addDays, formatTime, formatWeekdayShort, getMonthGrid, isSameDay, itemsOnDay, layoutDayItems, startOfDay, startOfWeek,
} from '../../lib/calendar-dates.js';
import { getTagStyle } from '../../lib/theme-colors.js';
import { Icon } from '../../ui/icon.js';
import { calendarItemColor, calendarItemTitle } from './calendar-event-detail.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/calendar.js').CalendarItem} CalendarItem */

const HOUR_HEIGHT = 48;
const FIRST_VISIBLE_HOUR = 7;
const MONTH_ITEMS_PER_DAY = 3;

/**
 * @param {{ item: CalendarItem, isSelected: boolean, onSelect: (item: CalendarItem) => void, showTime?: boolean, style?: Record<string, string> , className?: string }} props
 */
function CalendarItemChip({ item, isSelected, onSelect, showTime = true, style = {}, className = '' }) {
  const color = calendarItemColor(item);
  return html`<button
    type="button"
    class=${`calendar-item ${className}`}
    data-kind=${item.kind}
    data-canceled=${item.isCanceled ? 'true' : undefined}
    data-restricted=${item.isRestricted ? 'true' : undefined}
    data-selected=${isSelected ? 'true' : undefined}
    style=${{ ...getTagStyle(color), '--calendar-item-accent': `var(--t-color-${color})`, ...style }}
    title=${`${item.isFullDay ? t('calendar.allDay') : `${formatTime(item.startsAt)} - ${formatTime(item.endsAt)}`} ${calendarItemTitle(item)}`}
    onClick=${(event) => {
      event.stopPropagation();
      onSelect(item);
    }}
  >
    ${item.isRestricted ? html`<${Icon} name="IconLock" size=${12} />` : null}
    ${showTime && !item.isFullDay ? html`<span class="calendar-item-time">${formatTime(item.startsAt)}</span>` : null}
    <span class="calendar-item-title">${calendarItemTitle(item)}</span>
  </button>`;
}

/**
 * @param {{ anchor: Date, items: CalendarItem[], selectedId: string | null, onSelect: (item: CalendarItem) => void, onOpenDay: (day: Date) => void }} props
 */
export function MonthView({ anchor, items, selectedId, onSelect, onOpenDay }) {
  const days = getMonthGrid(anchor);
  const today = new Date();
  return html`<div class="calendar-month" role="grid" aria-label=${t('calendar.views.month')}>
    <div class="calendar-month-weekdays" role="row">
      ${days.slice(0, 7).map((day) => html`<div key=${day.getDay()} class="calendar-month-weekday" role="columnheader">${formatWeekdayShort(day)}</div>`)}
    </div>
    <div class="calendar-month-grid">
      ${days.map((day) => {
        const dayItems = itemsOnDay(items, day).sort((left, right) => Number(right.isFullDay) - Number(left.isFullDay) || left.startsAt.localeCompare(right.startsAt));
        const hiddenCount = Math.max(0, dayItems.length - MONTH_ITEMS_PER_DAY);
        return html`<div
          key=${day.toISOString()}
          class="calendar-month-day"
          role="gridcell"
          data-outside=${day.getMonth() !== anchor.getMonth() ? 'true' : undefined}
          data-today=${isSameDay(day, today) ? 'true' : undefined}
        >
          <button type="button" class="calendar-month-day-number" onClick=${() => onOpenDay(day)} aria-label=${day.toLocaleDateString('pt-BR')}>${day.getDate()}</button>
          <div class="calendar-month-day-items">
            ${dayItems.slice(0, MONTH_ITEMS_PER_DAY).map((item) => html`<${CalendarItemChip} key=${item.id} item=${item} isSelected=${selectedId === item.id} onSelect=${onSelect} />`)}
            ${hiddenCount > 0 ? html`<button type="button" class="calendar-month-more" onClick=${() => onOpenDay(day)}>${t('calendar.more', { count: hiddenCount })}</button>` : null}
          </div>
        </div>`;
      })}
    </div>
  </div>`;
}

/**
 * Semana (7 colunas) ou dia (1 coluna) com as horas na vertical.
 * @param {{ view: 'week' | 'day', anchor: Date, items: CalendarItem[], selectedId: string | null, onSelect: (item: CalendarItem) => void,
 *   onOpenDay: (day: Date) => void, onCreateAt: (startsAt: Date) => void }} props
 */
export function TimeGridView({ view, anchor, items, selectedId, onSelect, onOpenDay, onCreateAt }) {
  const days = view === 'day' ? [startOfDay(anchor)] : Array.from({ length: 7 }, (_, index) => addDays(startOfWeek(anchor), index));
  const scrollRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const today = new Date();
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = FIRST_VISIBLE_HOUR * HOUR_HEIGHT - 8;
  }, [view]);
  const allDayByDay = days.map((day) => itemsOnDay(items.filter((item) => item.isFullDay), day));
  const hasAllDay = allDayByDay.some((dayItems) => dayItems.length > 0);
  const nowMinutes = today.getHours() * 60 + today.getMinutes();

  return html`<div class="calendar-time-grid" data-view=${view} style=${{ '--calendar-day-count': String(days.length) }}>
    <div class="calendar-time-grid-header">
      <div class="calendar-time-grid-gutter"></div>
      ${days.map((day) => html`<button
        type="button"
        key=${day.toISOString()}
        class="calendar-time-grid-day-header"
        data-today=${isSameDay(day, today) ? 'true' : undefined}
        onClick=${() => onOpenDay(day)}
      >
        <span class="calendar-time-grid-weekday">${formatWeekdayShort(day)}</span>
        <span class="calendar-time-grid-date">${day.getDate()}</span>
      </button>`)}
    </div>
    ${hasAllDay
      ? html`<div class="calendar-time-grid-all-day">
          <div class="calendar-time-grid-gutter">${t('calendar.allDay')}</div>
          ${allDayByDay.map((dayItems, index) => html`<div key=${index} class="calendar-time-grid-all-day-cell">
            ${dayItems.map((item) => html`<${CalendarItemChip} key=${item.id} item=${item} isSelected=${selectedId === item.id} onSelect=${onSelect} showTime=${false} />`)}
          </div>`)}
        </div>`
      : null}
    <div class="calendar-time-grid-scroll" ref=${scrollRef}>
      <div class="calendar-time-grid-body" style=${{ height: `${24 * HOUR_HEIGHT}px` }}>
        <div class="calendar-time-grid-gutter calendar-time-grid-hours">
          ${Array.from({ length: 24 }, (_, hour) => html`<span key=${hour} class="calendar-time-grid-hour" style=${{ top: `${hour * HOUR_HEIGHT}px` }}>${hour === 0 ? '' : `${String(hour).padStart(2, '0')}:00`}</span>`)}
        </div>
        ${days.map((day) => {
          const positioned = layoutDayItems(itemsOnDay(items.filter((item) => !item.isFullDay), day), day);
          return html`<div
            key=${day.toISOString()}
            class="calendar-time-grid-column"
            data-today=${isSameDay(day, today) ? 'true' : undefined}
            onClick=${(event) => {
              const rect = /** @type {HTMLElement} */ (event.currentTarget).getBoundingClientRect();
              const minutes = Math.floor(((event.clientY - rect.top) / HOUR_HEIGHT) * 2) * 30;
              const startsAt = new Date(day);
              startsAt.setMinutes(Math.max(0, Math.min(23 * 60 + 30, minutes)));
              onCreateAt(startsAt);
            }}
          >
            ${isSameDay(day, today) ? html`<div class="calendar-time-grid-now" style=${{ top: `${(nowMinutes / 60) * HOUR_HEIGHT}px` }}></div>` : null}
            ${positioned.map(({ item, startMinute, endMinute, column, columnCount }) => html`<${CalendarItemChip}
              key=${item.id}
              item=${item}
              className="calendar-item-block"
              isSelected=${selectedId === item.id}
              onSelect=${onSelect}
              style=${{
                top: `${(startMinute / 60) * HOUR_HEIGHT}px`,
                height: `${Math.max(20, ((endMinute - startMinute) / 60) * HOUR_HEIGHT - 2)}px`,
                left: `calc(${(column / columnCount) * 100}% + 2px)`,
                width: `calc(${100 / columnCount}% - 4px)`,
              }}
            />`)}
          </div>`;
        })}
      </div>
    </div>
  </div>`;
}
